import React, { useState } from 'react';
import { DeleteIcon, FormEditIcon } from 'assets/icons';
import { Adjustment } from '../payrun.model';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import ImagePreview from 'components/image-preview/ImagePreview';
import { AttachmentFile } from 'types/baseTypes';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import AddAdjustmentModal, { CreateAdjustmentRequest } from './AddAdjustmentModal';
import { AdjustmentSourceType } from '../constants';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useCreateAdjustmentMutation, useDeleteAdjustmentMutation, useUpdateJobAdjustmentMutation } from '../api';
import { useAppSelector } from 'hooks/typedHooks';
import { GenericDeleteModal } from 'components';
import { Job } from 'containers/jobs/jobs.model';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../events';

interface PayAdjustmentsTableProps {
  adjustments: Adjustment[];
  job: Job;
  refetchAdjustments: () => void;
}

export const PayAdjustmentsTable: React.FC<PayAdjustmentsTableProps> = ({ adjustments, job, refetchAdjustments }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { managePayAdjustments, viewMyJobCommissionsAndPay } = currentTeamAccountingPermissions ?? {};

  const hasNoPermissions = !managePayAdjustments && !viewMyJobCommissionsAndPay;
  const hasPermissions = managePayAdjustments || viewMyJobCommissionsAndPay;

  const [createAdjustment] = useCreateAdjustmentMutation();
  const [deleteAdjustment] = useDeleteAdjustmentMutation();
  const [updateJobAdjustment] = useUpdateJobAdjustmentMutation();

  const [showImageViewer, setShowImageViewer] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<AttachmentFile[]>([]);
  const [isAddEditAdjustmentModalOpen, setIsAddEditAdjustmentModalOpen] = useState({
    isOpen: false,
    type: null
  });
  const [isDeleteAdjustmentModalOpen, setIsDeleteAdjustmentModalOpen] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState<Adjustment | null>(null);

  const canManageAdjustments = managePayAdjustments && job?.isEditable;

  const closePreview = () => {
    setReceiptUrl([]);
    setShowImageViewer(false);
  };

  const handleViewReceipt = (url: string, title: string) => {
    setReceiptUrl([{ url, title, urlLink: url }]);
    setShowImageViewer(true);
  };

  const handleAddAdjustment = () => {
    setIsAddEditAdjustmentModalOpen({ isOpen: true, type: 'add' });
  };

  const handleDeleteAdjustment = (adjustment: Adjustment) => {
    setSelectedAdjustment(adjustment);
    setIsDeleteAdjustmentModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedAdjustment) {
      try {
        await deleteAdjustment({
          adjustmentId: selectedAdjustment.id,
          teamId: currentTeam.id,
          jobId: job.id,
          merchantId: selectedAdjustment.merchantId
        }).unwrap();
        logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentRemoved, {
          merchantId: selectedAdjustment.merchantId,
          source: 'job-detail'
        });
        setIsDeleteAdjustmentModalOpen(false);
        refetchAdjustments();
      } catch (error) {
        // Error handling is done by the API's extraOptions
      }
    }
  };

  const handleSaveAdjustment = async (adjustmentData: CreateAdjustmentRequest) => {
    try {
      if (isAddEditAdjustmentModalOpen.type === 'edit') {
        await updateJobAdjustment({
          teamId: currentTeam.id,
          jobId: job.id,
          adjustmentId: selectedAdjustment?.id,
          payload: {
            title: adjustmentData.title,
            amount: adjustmentData.amount,
            postedDate: adjustmentData.date,
            notes: adjustmentData.description,
            receiptUrl: adjustmentData.receiptUrl,
            jobId: job.id,
            teamId: currentTeam.id,
            latestMerchantId: adjustmentData.latestMerchantId,
            currentMerchantId: adjustmentData.currentMerchantId
          }
        }).unwrap();
      } else {
        await createAdjustment({
          title: adjustmentData.title,
          amount: adjustmentData.amount,
          postedDate: adjustmentData.date,
          notes: adjustmentData.description,
          receiptUrl: adjustmentData.receiptUrl,
          jobId: job.id,
          teamId: currentTeam.id,
          type: AdjustmentSourceType.JOB,
          merchantId: adjustmentData.latestMerchantId
        }).unwrap();
      }

      setIsAddEditAdjustmentModalOpen({ isOpen: false, type: null });
      refetchAdjustments();
    } catch (error) {
      // Error handling is done by the API's extraOptions
    }
  };

  const handleEditAdjustment = (adjustment: Adjustment) => {
    setIsAddEditAdjustmentModalOpen({ isOpen: true, type: 'edit' });
    setSelectedAdjustment(adjustment);
  };

  return (
    <div className="w-full overflow-hidden rounded-lg border shadow-sm">
      <table className="w-full border-collapse bg-white">
        <thead className="border-b-2 bg-gray-50 text-sbase font-semibold text-[#878787]">
          <tr>
            <th className="py-2 px-4 text-left">Adjustment</th>
            <th className="py-2 px-4 text-left">Team</th>
            <th className="py-2 px-4 text-left">Date</th>
            <th className="py-2 px-4 text-left">Amount</th>
            <th className="py-2 px-4 text-left">Receipt</th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {hasPermissions &&
            adjustments.length > 0 &&
            adjustments.map(adjustment => (
              <tr key={adjustment.id} className="align-top">
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                  {adjustment.title}
                  {adjustment.notes && (
                    <div
                      className="mt-2 whitespace-pre-line text-px13 font-normal text-primaryText line-clamp-3"
                      title={adjustment.notes}>
                      {adjustment.notes}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">{adjustment.merchantName}</td>
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                  {DateUtils.getDateInFormat({
                    date: new Date(adjustment.postedDate),
                    dateFormat: 'MMM dd, yyyy'
                  })}
                </td>
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                  {getAmountWithCurrency(adjustment.amount)}
                </td>
                <td className="px-4 py-2 text-sbase font-semibold text-secondary">
                  {adjustment.receiptUrl && (
                    <button
                      onClick={() => handleViewReceipt(adjustment.receiptUrl, adjustment.title)}
                      className="text-secondary hover:underline">
                      View
                    </button>
                  )}
                </td>
                <td className="flex space-x-3 px-4 py-2 text-sm">
                  {canManageAdjustments && (
                    <>
                      <button
                        onClick={() => handleEditAdjustment(adjustment)}
                        className="transition-opacity hover:opacity-80">
                        <FormEditIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteAdjustment(adjustment)}
                        className="transition-opacity hover:opacity-80">
                        <DeleteIcon className="path path-fill-current h-5 w-5 text-accentText" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          {adjustments.length === 0 && hasNoPermissions && (
            <tr>
              <td colSpan={6} className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                You do not have permissions to view pay adjustments
              </td>
            </tr>
          )}
          {adjustments.length === 0 && hasPermissions && (
            <tr>
              <td colSpan={6} className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                No adjustments found
              </td>
            </tr>
          )}
          {canManageAdjustments && (
            <tr>
              <td colSpan={6} className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                <CustomButton
                  className="h-6 p-0"
                  id="add-adjustment-button"
                  type={ButtonType.GHOST}
                  onClick={handleAddAdjustment}
                  StartIcon={LineItemPlusReactIcon}>
                  Add adjustment
                </CustomButton>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {showImageViewer && <ImagePreview onClose={closePreview} images={receiptUrl} selectedIndex={0} />}
      {isAddEditAdjustmentModalOpen.isOpen && (
        <AddAdjustmentModal
          open={isAddEditAdjustmentModalOpen.isOpen}
          onClose={() => setIsAddEditAdjustmentModalOpen({ isOpen: false, type: null })}
          onSave={handleSaveAdjustment}
          isEdit={isAddEditAdjustmentModalOpen.type === 'edit'}
          selectedAdjustment={isAddEditAdjustmentModalOpen.type === 'edit' ? selectedAdjustment : undefined}
          sourceType={AdjustmentSourceType.JOB}
          assignees={job.assignees}
        />
      )}
      {isDeleteAdjustmentModalOpen && (
        <GenericDeleteModal
          isActionInProgress={false}
          onPrimaryBtnClick={handleConfirmDelete}
          onSecondaryBtnClick={() => setIsDeleteAdjustmentModalOpen(false)}
          primaryBtnId="delete-adjustment-confirm"
          primaryBtnLabel="Yes, Delete"
          secondaryBtnId="delete-adjustment-cancel"
          secondaryBtnLabel="No, Cancel"
          showModal={true}
          subTitle=""
          title="Are you sure you want to delete this adjustment?"
        />
      )}
    </div>
  );
};
